import { useEffect, useMemo, useState } from 'react';
import { X, Trophy, Shuffle, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEventMatches, useUpdateMatch } from '@/hooks/useMatches';
import {
  buildSeeding,
  buildRoundOne,
  totalRounds,
  shuffle,
  type Seed,
  type BracketMatch,
} from '@/lib/bracket-utils';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  isOwner: boolean;
}

type StoredMatch = {
  id?: string;            // db id once persisted
  round: number;
  index: number;
  player1: Seed | null;
  player2: Seed | null;
  winnerId?: string | null;
};

export function BracketSheet({ open, onClose, eventId, eventName, isOwner }: Props) {
  const { user } = useAuth();
  const { data: dbMatches, refetch } = useEventMatches(open ? eventId : null);
  const updateMatch = useUpdateMatch();
  const [generating, setGenerating] = useState(false);

  /** Convert db matches into our display structure. */
  const bracket = useMemo<StoredMatch[]>(() => {
    return (dbMatches || []).map(m => ({
      id: m.id,
      round: m.round || 1,
      index: m.bracket_index || 0,
      player1: m.player1_id || m.player1_label
        ? { id: m.player1_id || `lbl-${m.id}-1`, label: m.player1_username || m.player1_label || 'TBD', userId: m.player1_id }
        : null,
      player2: m.player2_id || m.player2_label
        ? { id: m.player2_id || `lbl-${m.id}-2`, label: m.player2_username || m.player2_label || 'TBD', userId: m.player2_id }
        : null,
      winnerId: m.winner_id,
    }));
  }, [dbMatches]);

  const rounds = useMemo(() => {
    const map = new Map<number, StoredMatch[]>();
    for (const m of bracket) {
      const arr = map.get(m.round) || [];
      arr.push(m);
      map.set(m.round, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.index - b.index);
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [bracket]);

  const handleGenerate = async () => {
    if (!user) return;
    setGenerating(true);
    try {
      // Pull RSVP'd users + their profiles
      const { data: rsvps } = await supabase.from('event_rsvps').select('user_id').eq('event_id', eventId);
      const userIds = (rsvps || []).map(r => r.user_id);
      if (userIds.length < 2) {
        toast.error('Need at least 2 RSVPs to generate a bracket');
        setGenerating(false);
        return;
      }
      const { data: profiles } = await supabase.from('profiles').select('user_id, username').in('user_id', userIds);
      const seeds: Seed[] = shuffle(
        (profiles || []).map(p => ({ id: p.user_id, label: p.username, userId: p.user_id })),
      );

      // Wipe prior bracket matches
      await supabase.from('matches').delete().eq('event_id', eventId);

      // Build round 1
      const ordered = buildSeeding(seeds);
      const round1 = buildRoundOne(ordered);

      const total = totalRounds(seeds.length);
      const inserts: any[] = [];

      // Round 1 (with possible byes auto-resolved)
      for (const m of round1) {
        const winnerId = m.player1 && !m.player2
          ? m.player1.userId ?? null
          : m.player2 && !m.player1
          ? m.player2.userId ?? null
          : null;
        inserts.push({
          event_id: eventId,
          created_by: user.id,
          player1_id: m.player1?.userId ?? null,
          player2_id: m.player2?.userId ?? null,
          player1_label: m.player1?.label ?? null,
          player2_label: m.player2?.label ?? null,
          winner_id: winnerId,
          round: 1,
          bracket_index: m.index,
        });
      }

      // Empty placeholders for later rounds
      for (let r = 2; r <= total; r++) {
        const matchesInRound = round1.length / 2 ** (r - 1);
        for (let i = 0; i < matchesInRound; i++) {
          inserts.push({
            event_id: eventId,
            created_by: user.id,
            round: r,
            bracket_index: i,
          });
        }
      }

      const { error } = await supabase.from('matches').insert(inserts);
      if (error) throw error;

      await supabase.from('events').update({
        bracket_status: 'generated',
        bracket_size: seeds.length,
      }).eq('id', eventId);

      toast.success(`Bracket generated for ${seeds.length} players!`);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate bracket');
    } finally {
      setGenerating(false);
    }
  };

  /** Mark a winner; advance them into the next-round slot if possible. */
  const handleSetWinner = async (m: StoredMatch, side: 1 | 2) => {
    if (!isOwner) {
      toast.error('Only the event creator can record results');
      return;
    }
    const winner = side === 1 ? m.player1 : m.player2;
    if (!m.id || !winner) return;
    try {
      await updateMatch.mutateAsync({ id: m.id, winner_id: winner.userId ?? null });
      // Find the parent match in next round (index = floor(this.index/2)).
      const nextRound = m.round + 1;
      const nextIdx = Math.floor(m.index / 2);
      const next = bracket.find(b => b.round === nextRound && b.index === nextIdx);
      if (next?.id) {
        const slot = m.index % 2 === 0 ? 'player1' : 'player2';
        await supabase.from('matches')
          .update(slot === 'player1'
            ? { player1_id: winner.userId ?? null, player1_label: winner.userId ? null : winner.label }
            : { player2_id: winner.userId ?? null, player2_label: winner.userId ? null : winner.label })
          .eq('id', next.id);
      } else if (nextRound > Math.max(...bracket.map(b => b.round))) {
        // Final match — mark event complete
        await supabase.from('events').update({ bracket_status: 'completed' }).eq('id', eventId);
        toast.success(`🏆 ${winner.label} wins the tournament!`);
      }
      refetch();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Auto-load when opening for the first time
  useEffect(() => {
    if (open) refetch();
  }, [open, refetch]);

  if (!open) return null;

  const hasBracket = bracket.length > 0;
  const champion = (() => {
    if (!hasBracket) return null;
    const lastRound = Math.max(...bracket.map(b => b.round));
    const final = bracket.find(b => b.round === lastRound);
    if (!final?.winnerId) return null;
    return final.winnerId === final.player1?.userId ? final.player1 : final.player2;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] h-[90vh] flex flex-col rounded-t-2xl border border-border bg-card" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <div>
            <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" /> Bracket
            </h3>
            <p className="text-[10px] text-muted-foreground font-body truncate max-w-[260px]">{eventName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {!hasBracket ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Trophy className="h-10 w-10 text-primary/40 mb-3" />
              <p className="text-sm text-foreground font-body mb-1">No bracket yet</p>
              <p className="text-xs text-muted-foreground font-body mb-4 max-w-xs">
                Generate a single-elimination bracket from RSVPs. Byes are added automatically when needed.
              </p>
              {isOwner ? (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground font-body disabled:opacity-50"
                >
                  <Shuffle className="h-3.5 w-3.5" />
                  {generating ? 'Generating...' : 'Generate Bracket'}
                </button>
              ) : (
                <p className="text-[11px] text-muted-foreground font-body">Waiting for the organizer to generate the bracket.</p>
              )}
            </div>
          ) : (
            <>
              {champion && (
                <div className="mb-4 rounded-xl border border-primary/40 bg-primary/10 p-3 text-center">
                  <Sparkles className="h-4 w-4 text-primary inline mr-1" />
                  <span className="text-sm font-semibold text-foreground font-body">Champion: {champion.label}</span>
                </div>
              )}
              {isOwner && (
                <div className="mb-3 flex justify-end">
                  <button
                    onClick={() => {
                      if (confirm('Regenerate the bracket? All current results will be lost.')) handleGenerate();
                    }}
                    className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-[10px] font-medium text-foreground font-body hover:bg-secondary/80 transition-colors"
                  >
                    <Shuffle className="h-3 w-3" /> Regenerate
                  </button>
                </div>
              )}
              <div className="flex gap-3 overflow-x-auto pb-2">
                {rounds.map(([roundNum, matches]) => (
                  <div key={roundNum} className="flex flex-col gap-3 min-w-[160px]">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body text-center">
                      {roundLabel(roundNum, rounds.length)}
                    </p>
                    {matches.map(m => (
                      <MatchSlot key={`${m.round}-${m.index}`} match={m} onPick={handleSetWinner} canPick={isOwner} />
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function roundLabel(round: number, totalRoundsCount: number): string {
  const remaining = totalRoundsCount - round;
  if (remaining === 0) return 'Final';
  if (remaining === 1) return 'Semis';
  if (remaining === 2) return 'Quarters';
  return `Round ${round}`;
}

function MatchSlot({
  match,
  onPick,
  canPick,
}: {
  match: StoredMatch;
  onPick: (m: StoredMatch, side: 1 | 2) => void;
  canPick: boolean;
}) {
  const winnerSide = match.winnerId
    ? (match.winnerId === match.player1?.userId ? 1 : match.winnerId === match.player2?.userId ? 2 : null)
    : null;
  return (
    <div className="rounded-lg border border-border bg-background/40 overflow-hidden text-[11px] font-body">
      {[1, 2].map(side => {
        const player = side === 1 ? match.player1 : match.player2;
        const isWinner = winnerSide === side;
        return (
          <button
            key={side}
            disabled={!canPick || !player || !match.player1 || !match.player2}
            onClick={() => onPick(match, side as 1 | 2)}
            className={`w-full flex items-center justify-between px-2.5 py-2 text-left border-b border-border last:border-b-0 transition-colors ${
              isWinner
                ? 'bg-ink-emerald/15 text-ink-emerald font-semibold'
                : 'text-foreground hover:bg-secondary/60'
            } disabled:cursor-default disabled:hover:bg-transparent disabled:text-muted-foreground`}
          >
            <span className="truncate flex-1">{player?.label || 'TBD'}</span>
            {isWinner && <Trophy className="h-3 w-3 ml-1 flex-shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
